import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFitnessWizard } from '../../contexts/FitnessWizardContext';
import { ThemedButton } from '../ThemedButton';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { CheckCircle2, User, Building2, Dumbbell, MapPin, Users, Shield, FileText, ShieldCheck, UserPlus, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getAttribution } from '../../utils/attribution';
import { trackGenerateLead } from '../../utils/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CLUB_TYPE_LABELS = {
  yoga_pilates: 'Yoga / Pilates',
  cycling_spin: 'Cycling / Spin',
  hiit_group: 'HIIT / Group Training',
  strength: 'Strength & Conditioning',
  boxing_kickboxing: 'Boxing / Kickboxing Fitness',
  dance_barre: 'Dance / Barre',
  personal_training: 'Personal Training Studio',
  wellness_recovery: 'Wellness / Recovery Studio',
  other: 'Other'
};

const SERVICE_LABELS = {
  group_classes: 'Group Classes',
  personal_training: '1:1 Personal Training',
  open_gym: 'Open Gym',
  youth_classes: 'Youth Classes',
  locker_rooms: 'Locker Rooms / Showers',
  sauna_cold_plunge: 'Sauna / Cold Plunge',
  other: 'Other'
};

const SummarySection = ({ icon: Icon, title, children }) => (
  <div className="border border-slate-200 rounded-lg overflow-hidden">
    <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 text-[#7B6FE8]" />
        <h3 className="font-semibold text-slate-900">{title}</h3>
      </div>
    </div>
    <div className="p-4 space-y-2">
      {children}
    </div>
  </div>
);

const SummaryItem = ({ label, value }) => (
  <div className="flex justify-between text-sm">
    <span className="text-slate-600">{label}</span>
    <span className="font-medium text-slate-900 text-right">{value || '—'}</span>
  </div>
);

const FitnessReviewSubmit = ({ affiliate }) => {
  const { formData, clearDraft } = useFitnessWizard();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [attribution] = useState(() => getAttribution());

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Clean up data before submission - convert empty strings to null for optional int fields
      const cleanData = { ...formData };
      
      // Convert empty strings to null for integer fields
      const intFields = ['active_members', 'average_daily_checkins', 'num_instructors', 'square_footage', 'youth_participant_count', 'year_business_started'];
      intFields.forEach(field => {
        if (cleanData[field] === '' || cleanData[field] === undefined) {
          cleanData[field] = null;
        } else if (typeof cleanData[field] === 'string') {
          cleanData[field] = parseInt(cleanData[field]) || null;
        }
      });
      
      const applicationData = {
        ...cleanData,
        affiliate_id: affiliate.id,
        affiliate_name: affiliate.name,
        flow_template: 'BOUTIQUE_FITNESS_V1',
        org_name: cleanData.business_dba || cleanData.business_legal_name,
        coverage_type: 'annual',
        gl_limit_per_occurrence: cleanData.gl_limit_per_occurrence || '$1,000,000',
        gl_limit_aggregate: cleanData.gl_limit_aggregate || '$2,000,000',
        utm_source: attribution?.utm_source ?? null,
        utm_medium: attribution?.utm_medium ?? null,
        utm_campaign: attribution?.utm_campaign ?? null
      };
      
      const response = await axios.post(`${API_URL}/applications`, applicationData);
      trackGenerateLead();
      clearDraft();
      toast.success('Application submitted successfully!');
      navigate(`/apply-fitness/${affiliate.slug}/success`);
    } catch (error) {
      console.error('Submission error:', error);
      console.error('Error response:', error.response?.data);
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to submit application. Please try again.';
      if (errorDetail) {
        if (Array.isArray(errorDetail)) {
          errorMessage = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof errorDetail === 'string') {
          errorMessage = errorDetail;
        }
      }
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const getClubTypeLabel = () => {
    if (formData.fitness_club_type === 'other') {
      return formData.fitness_club_type_other || 'Other';
    }
    return CLUB_TYPE_LABELS[formData.fitness_club_type] || formData.fitness_club_type;
  };

  const getServicesLabel = () => {
    return formData.fitness_services
      .map(s => s === 'other' ? formData.fitness_services_other : SERVICE_LABELS[s])
      .filter(Boolean)
      .join(', ');
  };

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="bg-[#232323] text-white rounded-t-xl">
        <div className="flex items-center gap-3">
          <Send className="h-6 w-6" />
          <div>
            <CardTitle className="text-xl">Review & Submit</CardTitle>
            <CardDescription className="text-white/60">
              Please review your application before submitting
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6 space-y-4">
        {/* Contact Information */}
        <SummarySection icon={User} title="Primary Contact">
          <SummaryItem label="Name" value={`${formData.first_name} ${formData.last_name}`} />
          <SummaryItem label="Email" value={formData.applicant_email} />
          <SummaryItem label="Phone" value={formData.applicant_phone} />
          {formData.applicant_title && <SummaryItem label="Title" value={formData.applicant_title} />}
        </SummarySection>

        {/* Business Information */}
        <SummarySection icon={Building2} title="Business Information">
          <SummaryItem label="Legal Name" value={formData.business_legal_name} />
          {formData.business_dba && <SummaryItem label="DBA" value={formData.business_dba} />}
          <SummaryItem label="Address" value={`${formData.street_address}, ${formData.org_city}, ${formData.org_state} ${formData.postal_code}`} />
          <SummaryItem label="Year Started" value={formData.year_business_started} />
          {formData.website && <SummaryItem label="Website" value={formData.website} />}
        </SummarySection>

        {/* Club Type & Services */}
        <SummarySection icon={Dumbbell} title="Fitness Club Type & Services">
          <SummaryItem label="Business Type" value={getClubTypeLabel()} />
          <SummaryItem label="Services" value={getServicesLabel()} />
        </SummarySection>

        {/* Locations */}
        <SummarySection icon={MapPin} title="Locations & Facilities">
          <SummaryItem label="Location Type" value={formData.location_type} />
          <SummaryItem label="Facility Type" value={formData.facility_type?.replace('_', ' ')} />
          {formData.locations.map((loc, idx) => (
            <SummaryItem 
              key={idx} 
              label={`Location ${idx + 1}`} 
              value={`${loc.address}, ${loc.city}, ${loc.state} ${loc.zip}`} 
            />
          ))}
          {formData.square_footage && <SummaryItem label="Square Footage" value={`${formData.square_footage} sq ft`} />}
        </SummarySection>

        {/* Membership */}
        <SummarySection icon={Users} title="Membership & Staff">
          <SummaryItem label="Active Members" value={formData.active_members} />
          <SummaryItem label="Daily Check-ins" value={formData.average_daily_checkins} />
          <SummaryItem label="Instructors/Trainers" value={formData.num_instructors} />
          <SummaryItem label="Youth Participants" value={formData.has_youth_participants ? `Yes (${formData.youth_participant_count || 'count not specified'})` : 'No'} />
        </SummarySection>

        {/* Coverage */}
        <SummarySection icon={Shield} title="Coverage Details">
          <SummaryItem label="Policy Effective Date" value={formData.policy_effective_date} />
          <SummaryItem label="Policy Term" value="12 months" />
          <SummaryItem label="GL Limits" value={`${formData.gl_limit_per_occurrence} / ${formData.gl_limit_aggregate}`} />
          {formData.requested_optional_coverages?.length > 0 && (
            <SummaryItem label="Optional Interests" value={formData.requested_optional_coverages.join(', ')} />
          )}
        </SummarySection>

        {/* Claims History */}
        <SummarySection icon={FileText} title="Claims History">
          <SummaryItem label="Prior Claims" value={formData.claims_history_flag ? 'Yes' : 'No'} />
          {formData.claims_history_flag && formData.claims_notes && (
            <div className="mt-2 p-3 bg-amber-50 rounded text-sm text-amber-800">
              {formData.claims_notes}
            </div>
          )}
        </SummarySection>

        {/* Fraud Warranty */}
        <SummarySection icon={ShieldCheck} title="Fraud Warranty">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <span className="text-sm text-green-800 font-medium">Fraud warranty accepted</span>
          </div>
        </SummarySection>

        {/* Additional Insured */}
        <SummarySection icon={UserPlus} title="Additional Insured">
          <SummaryItem label="Additional Insured Required" value={formData.additional_insured_required ? 'Yes' : 'No'} />
          {formData.additional_insured_required && formData.additional_insured_details && (
            <div className="mt-2 p-3 bg-slate-50 rounded text-sm text-slate-700 whitespace-pre-line">
              {formData.additional_insured_details}
            </div>
          )}
        </SummarySection>

        {/* Submit Button */}
        <div className="pt-4">
          {/* Hidden lead-source attribution fields (auto-populated before submit) */}
          <input type="hidden" name="utm_source" data-testid="fitness-utm-source-hidden" value={attribution?.utm_source ?? 'direct'} readOnly />
          <input type="hidden" name="utm_medium" data-testid="fitness-utm-medium-hidden" value={attribution?.utm_medium ?? 'direct'} readOnly />
          <input type="hidden" name="utm_campaign" data-testid="fitness-utm-campaign-hidden" value={attribution?.utm_campaign ?? 'direct'} readOnly />
          <ThemedButton 
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="fitness-submit-btn"
            variant="wizard" size="full"
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                Submitting...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Send className="h-5 w-5" />
                Submit Application
              </span>
            )}
          </ThemedButton>
          <p className="text-xs text-slate-500 text-center mt-3">
            By submitting, you agree to our terms of service and privacy policy.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default FitnessReviewSubmit;
